/**
 * Turns the plain-text knowledge Arthur was given back into rows we can lay
 * out. The text is built by the backend (`knowledge_gaps/corpus.py`) as one
 * `Label: value` line per fact; free-text values such as parking notes can
 * span several lines and contain colons of their own, so only the labels the
 * backend writes start a new fact.
 */

export interface Fact {
  label: string;
  value: string;
}

export interface StoreInfoFacts {
  /** The yes/no capabilities, relabelled as the service they describe. */
  services: Array<{ label: string; offered: boolean }>;
  details: Fact[];
}

const SERVICE_LABELS: Record<string, string> = {
  'Takes reservations': 'Reservations',
  'Takes catering': 'Catering',
  'Takes events': 'Events',
  'Gift cards': 'Gift cards',
  'Outdoor seating': 'Outdoor seating',
  Delivery: 'Delivery',
  'Phone orders': 'Phone orders',
};

const DETAIL_LABELS = [
  'Address',
  'Phone',
  'Website',
  'Cuisine',
  'Parking',
  'Accessibility',
  'Delivery instructions',
  'Ordering policy',
];

const STORE_INFO_LABELS = [...Object.keys(SERVICE_LABELS), ...DETAIL_LABELS];

/** `null` when the text isn't in the shape we expect; show it as-is. */
export function parseStoreInfo(content: string): StoreInfoFacts | null {
  const facts: Fact[] = [];
  for (const line of content.split('\n')) {
    const label = STORE_INFO_LABELS.find((l) => line.startsWith(`${l}: `));
    if (label) {
      facts.push({ label, value: line.slice(label.length + 2) });
    } else if (facts.length > 0) {
      facts[facts.length - 1].value += `\n${line}`;
    } else if (line.trim()) {
      return null;
    }
  }
  if (facts.length === 0) return null;

  const result: StoreInfoFacts = { services: [], details: [] };
  for (const { label, value } of facts) {
    const service = SERVICE_LABELS[label];
    const flag = value.trim();
    if (service && (flag === 'yes' || flag === 'no')) {
      result.services.push({ label: service, offered: flag === 'yes' });
    } else {
      result.details.push({ label, value: value.trim() });
    }
  }
  return result;
}

/**
 * Hours are one `Day: time` (or `Date Holiday: time — note`) line each.
 * `null` if any line doesn't split, so nothing is silently dropped.
 */
export function parseHours(content: string): Fact[] | null {
  const rows: Fact[] = [];
  for (const line of content.split('\n')) {
    if (!line.trim()) continue;
    const at = line.indexOf(': ');
    if (at <= 0) return null;
    rows.push({ label: line.slice(0, at), value: line.slice(at + 2) });
  }
  return rows.length > 0 ? rows : null;
}
